import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { HelpTip } from "@/components/HelpTip";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from "recharts";
import {
  Brain, RefreshCw, Printer, TrendingUp, Users, CheckCircle,
  AlertTriangle, Clock, FolderKanban, Target, ChevronRight, Loader2, X,
  Filter, ChevronDown, FlaskConical
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const COLORS = ["#007934", "#4CAF50", "#8BC34A", "#FFCA28", "#EF5350", "#00ACC1", "#7C3AED", "#F97316"];
const STATUS_COLOR = {
  Active: "bg-green-100 text-green-700", active: "bg-green-100 text-green-700", Activo: "bg-green-100 text-green-700",
  New: "bg-blue-100 text-blue-700", Nuevo: "bg-blue-100 text-blue-700",
  Closed: "bg-gray-100 text-gray-600", closed: "bg-gray-100 text-gray-600",
  Resolved: "bg-purple-100 text-purple-700", Resuelto: "bg-purple-100 text-purple-700",
  Removed: "bg-red-100 text-red-600",
};

export default function Executive() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [genLoading, setGenLoading] = useState(false);
  const [reportHtml, setReportHtml] = useState(null);
  const [showReport, setShowReport] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ project_id: "", assignee: "" });
  const [pendingFilters, setPendingFilters] = useState({ project_id: "", assignee: "" });
  const [allProjects, setAllProjects] = useState([]);
  const [incluirEjemplos, setIncluirEjemplos] = useState(false);
  const reportRef = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(async (flt = filters, incEj = incluirEjemplos) => {
    setLoading(true);
    try {
      const params = {};
      if (flt.project_id) params.project_id = flt.project_id;
      if (flt.assignee) params.assignee = flt.assignee;
      if (incEj) params.incluir_ejemplos = true;
      const { data: d } = await axios.get(`${API}/executive/overview`, { params });
      setData(d);
      setLoadError("");
      if (!flt.project_id && !flt.assignee) {
        setAllProjects(d.projects || []);
      }
    } catch (e) {
      console.error(e);
      setLoadError(e.response?.data?.detail || e.message || "No se pudo cargar el Cuadro de Mando Ejecutivo.");
    }
    finally { setLoading(false); }
  }, [filters, incluirEjemplos]);

  useEffect(() => { load(); }, []); // eslint-disable-line

  const toggleEjemplos = () => {
    const next = !incluirEjemplos;
    setIncluirEjemplos(next);
    load(filters, next);
  };

  const applyFilters = () => {
    setFilters(pendingFilters);
    setShowFilters(false);
    load(pendingFilters);
  };
  const resetFilters = () => {
    const empty = { project_id: "", assignee: "" };
    setPendingFilters(empty);
    setFilters(empty);
    setShowFilters(false);
    load(empty);
  };
  const hasFilters = filters.project_id || filters.assignee;

  const generateAIReport = async () => {
    setGenLoading(true);
    try {
      const { data: rep } = await axios.post(`${API}/reports/generate`, {
        title: `Informe Ejecutivo Comité de Dirección – ${new Date().toLocaleDateString("es-ES")}`,
        type: "executive",
        project_ids: [],
      });
      setReportHtml(rep.html_content);
      setShowReport(true);
    } catch (e) { alert("Error generando el informe"); }
    finally { setGenLoading(false); }
  };

  const printReport = () => {
    const blob = new Blob([reportHtml], { type: "text/html; charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, "_blank");
    if (win) win.onload = () => { win.print(); setTimeout(() => URL.revokeObjectURL(url), 5000); }
    else URL.revokeObjectURL(url);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 size={32} className="animate-spin text-[#007934]" />
    </div>
  );
  if (loadError) return (
    <div className="flex flex-col items-center justify-center h-64 gap-3" data-testid="executive-error">
      <AlertTriangle size={28} className="text-rose-500" />
      <p className="text-sm text-gray-600">No se pudo cargar el Cuadro de Mando Ejecutivo.</p>
      <p className="text-xs text-gray-400">{loadError}</p>
      <button onClick={() => load()} className="text-sm bg-[#007934] text-white px-4 py-2 rounded-lg font-medium hover:bg-[#006528]" data-testid="executive-retry-btn">
        Reintentar
      </button>
    </div>
  );
  if (!data) return null;

  const { global: g, budget, projects, assignees, by_type, by_status, active_items, last_sync } = data;

  // Normalize status data: merge similar statuses
  const statusMerged = {};
  const statusMap = {
    Active: "Activo", active: "Activo", Activo: "Activo",
    New: "Nuevo", Nuevo: "Nuevo",
    Closed: "Cerrado", closed: "Cerrado", Resuelto: "Cerrado", Resolved: "Cerrado", Done: "Cerrado",
    Removed: "Eliminado", Analyzing: "En análisis",
  };
  by_status.forEach(({ status, count }) => {
    const key = statusMap[status] || status;
    statusMerged[key] = (statusMerged[key] || 0) + count;
  });
  const statusChartData = Object.entries(statusMerged)
    .map(([status, count]) => ({ status, count }))
    .sort((a, b) => b.count - a.count);

  const typeChartData = by_type.slice(0, 7);

  const budgetPct = Math.round((budget.total_spent / budget.max_total) * 100);

  // Iter313 · O05 — separar presupuesto corporativo IT de PBL de contratos
  // comerciales: antes se mezclaban en el mismo gráfico (un proyecto
  // comercial con PBL de 81M€ aplastaba la escala de proyectos IT reales
  // de 15-120K€). Los proyectos type="commercial" (vinculados a una oferta
  // ganada) se excluyen de este gráfico — su presupuesto es el PBL del
  // contrato, no una partida del presupuesto IT de 460K€.
  const commercialLinkedProjects = projects.filter(p => p.type === "commercial" && p.budget_allocated > 0);
  const projBudgetData = projects
    .filter(p => p.budget_allocated > 0 && p.type !== "commercial")
    .map(p => ({
      name: p.title.length > 22 ? p.title.slice(0, 22) + "…" : p.title,
      asignado: p.budget_allocated,
      gastado: p.budget_spent,
    }));

  return (
    <div className="space-y-6 w-full" data-testid="executive-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-['Outfit']">Cuadro de Mando Ejecutivo</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Plataformas Corporativas Valoriza
            {last_sync && <span className="ml-2 text-xs text-gray-400">· Última sync: {new Date(last_sync.started_at).toLocaleString("es-ES")}</span>}
            {!incluirEjemplos && data?.ejemplos_excluidos && (data.ejemplos_excluidos.proyectos > 0 || data.ejemplos_excluidos.workitems > 0) && (
              <span className="ml-2 text-xs text-amber-600" data-testid="exec-ejemplos-excluidos-note">
                · {data.ejemplos_excluidos.proyectos} proyectos y {data.ejemplos_excluidos.workitems} tareas de prueba excluidas de estas métricas
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Plan cierre POC/Comité P0-01 · ver/ocultar proyectos y workitems de prueba */}
          <button
            onClick={toggleEjemplos}
            data-testid="exec-toggle-ejemplos-btn"
            title={incluirEjemplos ? "Ocultar datos de prueba/ejemplo de nuevo" : "Incluir temporalmente proyectos/tareas de prueba en las métricas"}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-all ${incluirEjemplos ? "bg-amber-500 text-white border-amber-500" : "bg-white text-gray-500 border-gray-200 hover:border-gray-300"}`}
          >
            <FlaskConical size={13} />
            {incluirEjemplos ? "Viendo ejemplos" : "Ver ejemplos"}
          </button>
          {/* Filter button */}
          <div className="relative">
            <button
              onClick={() => { setPendingFilters(filters); setShowFilters(!showFilters); }}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-all ${hasFilters ? "bg-[#007934] text-white border-[#007934]" : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"}`}
              data-testid="exec-filter-btn"
            >
              <Filter size={14} />
              Filtros
              {hasFilters && <span className="w-1.5 h-1.5 rounded-full bg-white ml-0.5" />}
              <ChevronDown size={12} />
            </button>
            {showFilters && (
              <div className="absolute right-0 top-10 w-72 bg-white rounded-xl border border-gray-200 shadow-xl z-30 p-4 space-y-3" data-testid="exec-filter-panel">
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Proyecto</label>
                  <select
                    value={pendingFilters.project_id}
                    onChange={e => setPendingFilters({ ...pendingFilters, project_id: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
                    data-testid="filter-project-select"
                  >
                    <option value="">Todos los proyectos</option>
                    {allProjects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Persona / Asignado</label>
                  <input
                    type="text"
                    placeholder="Buscar por nombre..."
                    value={pendingFilters.assignee}
                    onChange={e => setPendingFilters({ ...pendingFilters, assignee: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
                    data-testid="filter-assignee-input"
                  />
                </div>
                <div className="flex gap-2 pt-1">
                  <button onClick={resetFilters} className="flex-1 text-sm text-gray-500 border border-gray-200 rounded-lg py-2 hover:bg-gray-50" data-testid="reset-filters-btn">Resetear</button>
                  <button onClick={applyFilters} className="flex-1 text-sm bg-[#007934] text-white rounded-lg py-2 font-medium hover:bg-[#006528]" data-testid="apply-filters-btn">Aplicar</button>
                </div>
              </div>
            )}
          </div>
          <button onClick={() => load(filters)} className="p-2 text-gray-400 hover:text-gray-600 bg-white rounded-lg border border-gray-100 hover:border-gray-200 transition-all" title="Actualizar">
            <RefreshCw size={16} />
          </button>
          <button
            onClick={generateAIReport}
            disabled={genLoading}
            className="flex items-center gap-2 bg-gradient-to-r from-[#007934] to-[#4CAF50] text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:opacity-90 transition-all shadow-md disabled:opacity-60"
            data-testid="generate-ai-report-btn"
          >
            {genLoading ? <Loader2 size={15} className="animate-spin" /> : <Brain size={15} />}
            {genLoading ? "Generando presentación…" : "Generar informe Pando"}
          </button>
        </div>
      </div>

      {/* Active filters banner */}
      {hasFilters && (
        <div className="flex items-center gap-2 text-sm bg-[#E8F5E9] border border-[#C8E6C9] text-[#007934] px-4 py-2.5 rounded-lg" data-testid="active-filters-banner">
          <Filter size={14} />
          <span>Filtros activos:</span>
          {filters.project_id && <span className="bg-white px-2 py-0.5 rounded-full text-xs font-medium">{allProjects.find(p => p.id === filters.project_id)?.title || filters.project_id}</span>}
          {filters.assignee && <span className="bg-white px-2 py-0.5 rounded-full text-xs font-medium">{filters.assignee}</span>}
          <button onClick={resetFilters} className="ml-auto text-xs underline hover:no-underline">Limpiar filtros</button>
        </div>
      )}

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3" data-testid="exec-kpi-strip">
        {[
          { label: "Work Items Total", val: g.total_wi, icon: FolderKanban, color: "text-green-600 bg-green-50" },
          { label: "En Activo", val: g.active_wi, icon: TrendingUp, color: "text-blue-600 bg-blue-50" },
          { label: "Completados", val: g.done_wi, icon: CheckCircle, color: "text-emerald-600 bg-emerald-50" },
          { label: "Tasa Compl.", val: `${g.completion_rate}%`, icon: Target, color: "text-purple-600 bg-purple-50" },
          { label: "Bugs Activos", val: g.bugs, icon: AlertTriangle, color: "text-red-600 bg-red-50" },
          { label: "Horas Est.", val: `${(g.total_hours/1000).toFixed(1)}K`, icon: Clock, color: "text-orange-600 bg-orange-50" },
          { label: "En plantilla", val: g.active_roster ?? 0, icon: Users, color: "text-teal-600 bg-teal-50", testid: "exec-kpi-active-roster" },
          { label: "Con tareas asignadas", val: g.people_with_tasks ?? 0, icon: Users, color: "text-indigo-600 bg-indigo-50", testid: "exec-kpi-people-with-tasks" },
        ].map(({ label, val, icon: Icon, color, testid }) => (
          <div key={label} className="bg-white rounded-xl border border-gray-100 p-4 text-center card-hover" data-testid={testid}>
            <div className={`w-9 h-9 rounded-lg ${color} flex items-center justify-center mx-auto mb-2`}>
              <Icon size={16} className={color.split(" ")[0]} />
            </div>
            <p className="text-xl font-bold text-gray-900 font-['Outfit']">{val}</p>
            <p className="text-xs text-gray-500 mt-0.5 leading-tight">{label}</p>
          </div>
        ))}
      </div>

      {/* Projects table + Budget bar */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Projects overview table */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 p-5" data-testid="exec-projects-table">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900 font-['Outfit']">Estado por Proyecto</h3>
            <button onClick={() => navigate("/projects")} className="text-xs text-[#007934] hover:underline flex items-center gap-1">
              Ver proyectos <ChevronRight size={11} />
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-400 uppercase border-b border-gray-50">
                  <th className="text-left pb-2 pr-3">Proyecto</th>
                  <th className="text-left pb-2 pr-3">Resp. Técnico</th>
                  <th className="text-right pb-2 pr-3">WIs</th>
                  <th className="text-right pb-2 pr-3">Activos</th>
                  <th className="text-right pb-2 pr-3">Completados</th>
                  <th className="text-right pb-2 pr-3">Horas</th>
                  <th className="text-left pb-2">Entrega</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {projects.map(p => (
                  <tr key={p.id} className="hover:bg-gray-50 cursor-pointer transition-colors" onClick={() => navigate(`/projects/${p.id}`)}>
                    <td className="py-3 pr-3">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: p.status === "active" ? "#007934" : p.status === "paused" ? "#FFCA28" : "#EF5350" }} />
                        <span className="font-medium text-gray-900 text-xs">{p.title}</span>
                      </div>
                    </td>
                    <td className="py-3 pr-3 text-xs text-gray-500">{p.responsible_tech || "—"}</td>
                    <td className="py-3 pr-3 text-xs text-right font-semibold text-gray-800">{p.total_wi}</td>
                    <td className="py-3 pr-3 text-xs text-right text-blue-600 font-medium">{p.active_wi}</td>
                    <td className="py-3 pr-3 text-xs text-right text-green-600 font-medium">{p.done_wi}</td>
                    <td className="py-3 pr-3 text-xs text-right text-gray-600">{p.total_hours > 0 ? `${p.total_hours}h` : "—"}</td>
                    <td className="py-3 text-xs text-gray-500">{p.end_date || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Budget summary */}
        <div className="bg-white rounded-xl border border-gray-100 p-5" data-testid="exec-budget-card">
          <h3 className="font-semibold text-gray-900 font-['Outfit'] mb-4 flex items-center gap-1.5">
            Ejecución Presupuestaria
            <HelpTip text="Presupuesto corporativo IT (licencias + externos + internos). No incluye el PBL de proyectos vinculados a contratos comerciales — ese se gestiona en Ofertas Comercial." />
          </h3>
          <div className="text-center mb-4">
            <p className="text-3xl font-bold text-[#007934] font-['Outfit']">{budgetPct}%</p>
            <p className="text-xs text-gray-500 mt-1">ejecutado de {(budget.max_total/1000).toFixed(0)}K€</p>
          </div>
          <div className="h-2 bg-gray-100 rounded-full mb-4">
            <div className="h-full bg-gradient-to-r from-[#007934] to-[#4CAF50] rounded-full" style={{ width: `${Math.min(budgetPct, 100)}%` }} />
          </div>
          <div className="space-y-2">
            {[
              ["Licencias", budget.spent_licencias, budget.budget_licencias],
              ["Externos", budget.spent_external, budget.budget_external],
              ["Internos", budget.spent_internal, budget.budget_internal],
            ].map(([cat, spent, total]) => {
              const pct = total > 0 ? Math.round(spent / total * 100) : 0;
              return (
                <div key={cat}>
                  <div className="flex justify-between text-xs mb-0.5">
                    <span className="text-gray-600">{cat}</span>
                    <span className="font-medium text-gray-800">{pct}%</span>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full">
                    <div className="h-full bg-[#007934] rounded-full" style={{ width: `${Math.min(pct, 100)}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
          {projBudgetData.length > 0 && (
            <div className="mt-4 h-32">
              <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">Por proyecto (sólo IT — excluye contratos comerciales)</p>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={projBudgetData} layout="vertical" barCategoryGap="20%">
                  <XAxis type="number" tick={{ fontSize: 9 }} axisLine={false} tickLine={false} tickFormatter={v => `${(v/1000).toFixed(0)}K`} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 9 }} axisLine={false} tickLine={false} width={70} />
                  <Tooltip formatter={v => [`${v.toLocaleString("es-ES")}€`]} />
                  <Bar dataKey="asignado" name="Asignado" fill="#e5e7eb" radius={[0,3,3,0]} />
                  <Bar dataKey="gastado" name="Gastado" fill="#007934" radius={[0,3,3,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          {commercialLinkedProjects.length > 0 && (
            <p className="text-[10px] text-gray-400 mt-2" data-testid="exec-commercial-pbl-note">
              + {commercialLinkedProjects.length} proyecto{commercialLinkedProjects.length === 1 ? "" : "s"} con work items vinculado{commercialLinkedProjects.length === 1 ? "" : "s"} a contrato{commercialLinkedProjects.length === 1 ? "" : "s"} comercial{commercialLinkedProjects.length === 1 ? "" : "es"} — su presupuesto es el PBL del contrato, no presupuesto IT (se gestiona en Ofertas Comercial, no en este gráfico).
            </p>
          )}
        </div>
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* By type */}
        <div className="bg-white rounded-xl border border-gray-100 p-5" data-testid="exec-type-chart">
          <h3 className="font-semibold text-gray-900 font-['Outfit'] mb-4">Work Items por Tipo</h3>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={typeChartData} barCategoryGap="30%">
                <XAxis dataKey="type" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip />
                <Bar dataKey="count" name="Items" fill="#007934" radius={[4,4,0,0]}>
                  {typeChartData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* By status pie */}
        <div className="bg-white rounded-xl border border-gray-100 p-5" data-testid="exec-status-chart">
          <h3 className="font-semibold text-gray-900 font-['Outfit'] mb-4">Distribución por Estado</h3>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={statusChartData} dataKey="count" nameKey="status" cx="50%" cy="50%"
                  innerRadius={45} outerRadius={80} paddingAngle={3}>
                  {statusChartData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Legend iconSize={10} formatter={(v) => <span style={{ fontSize: 11 }}>{v}</span>} />
                <Tooltip formatter={(v, n) => [v, n]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Assignees table + Active items */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* By assignee */}
        <div className="bg-white rounded-xl border border-gray-100 p-5" data-testid="exec-assignees-table">
          <h3 className="font-semibold text-gray-900 font-['Outfit'] mb-4">Carga de Trabajo por Persona</h3>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {assignees.slice(0, 12).map((a, i) => {
              const pct = a.total > 0 ? Math.round((a.done / a.total) * 100) : 0;
              return (
                <div key={a.name} className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-full bg-[#E8F5E9] flex items-center justify-center text-[#007934] text-xs font-bold flex-shrink-0">
                    {a.name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-medium text-gray-900 truncate">{a.name}</span>
                      <span className="text-xs text-gray-500 ml-2 flex-shrink-0">{a.total} items · {a.hours > 0 ? `${a.hours}h` : "—"}</span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <div className="flex-1 h-1 bg-gray-100 rounded-full">
                        <div className="h-full bg-[#007934] rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-xs text-gray-400 flex-shrink-0">{pct}%</span>
                      {a.bugs > 0 && <span className="text-xs text-red-500 flex-shrink-0">{a.bugs} bug{a.bugs > 1 ? "s" : ""}</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Active work items */}
        <div className="bg-white rounded-xl border border-gray-100 p-5" data-testid="exec-active-items">
          <h3 className="font-semibold text-gray-900 font-['Outfit'] mb-4">En Progreso Ahora ({active_items.length})</h3>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {active_items.slice(0, 15).map(wi => (
              <div key={wi.id} className="flex items-start gap-2 p-2 rounded-lg hover:bg-gray-50 transition-colors">
                <span className={`text-xs px-1.5 py-0.5 rounded flex-shrink-0 mt-0.5 ${STATUS_COLOR[wi.status] || "bg-gray-100 text-gray-600"}`}>
                  {wi.type?.slice(0, 4) || "?"}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-900 leading-tight line-clamp-2">{wi.title}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{wi.assigned_to || "Sin asignar"}{wi.estimated_hours > 0 ? ` · ${wi.estimated_hours}h` : ""}</p>
                </div>
              </div>
            ))}
            {active_items.length === 0 && (
              <p className="text-sm text-gray-400 text-center py-8">No hay items activos</p>
            )}
          </div>
        </div>
      </div>

      {/* AI Report Modal */}
      {showReport && reportHtml && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="ai-report-modal">
          <div className="bg-white rounded-2xl w-full max-w-5xl h-[90vh] flex flex-col shadow-2xl">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-base font-bold font-['Outfit'] flex items-center gap-2">
                <Brain size={18} className="text-[#007934]" />
                Informe Ejecutivo — Perplexity AI
              </h2>
              <div className="flex items-center gap-2">
                <button onClick={printReport} className="flex items-center gap-1.5 text-sm text-gray-600 border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-50" data-testid="print-report-btn">
                  <Printer size={14} /> Imprimir / PDF
                </button>
                <button onClick={() => setShowReport(false)} className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100">
                  <X size={18} />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-1">
              <iframe
                srcDoc={reportHtml}
                className="w-full h-full border-0 rounded-xl"
                title="Informe Ejecutivo"
                data-testid="report-iframe"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
